import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Navigate, useNavigate, useParams } from 'react-router';
import { useQuery } from '@tanstack/react-query';
import {
  BRIEF_TYPE_LABEL,
  useUpdateAnyBriefCustomColumnValueMutation,
  type Brief,
} from '@/entities/brief';
import {
  organizationTrackersQueryOptions,
  trackerOpenFailure,
  trackerQueryOptions,
  type Tracker,
  type TrackerViewType,
} from '@/entities/tracker';
import { useCurrentWorkspaceId } from '@/entities/workspace';
import { Button, Empty, EmptyContent, EmptyDescription, EmptyHeader } from '@monksflow/monks-ui';
import { BreadcrumbPageHeader, ListPageBody, ListPageLayout } from '@/shared/ui/layout';
import { StaggerItem } from '@/shared/ui/animations';
import { ROUTES } from '@/shared/config/routes';
import { track } from '@/shared/analytics';
import { EmptyStateTitle } from '@/shared/ui';
import { trackersPageTitle } from './constants/trackers.constants';
import { TrackerBoardHeader } from './TrackerBoardHeader';
import { canEditTracker } from './hooks/useTrackerAccess';
import {
  boardTypeOf,
  BoardViewTabs,
  CustomTrackerBoard,
  DashboardView,
  GROUP_COLORS,
  isRecordSource,
  KanbanView,
  newlyNarrowingColumnIds,
  RecordTrackerBoard,
  TrackerAccessProvider,
  TrackerCommentsPanel,
  TrackersBoardTable,
  TrackersEmptyState,
  TrackersToolbar,
  StatusConfigModal,
  ButtonConfigModal,
  FormulaConfigModal,
  TimelineView,
  useActiveView,
  useCommentDeeplink,
  useTrackersBoard,
} from '@/widgets/trackers-board';
import { useTranslation } from 'react-i18next';

/**
 * View management for the briefs board — enabled views live on the shared
 * layout (`board.views`); the active view is session-local and defaults to the
 * first enabled view. `table` is pinned (non-removable).
 */
function useBriefsBoardViews(
  trackerId: string,
  views: TrackerViewType[],
  setViews: (next: TrackerViewType[]) => void,
) {
  const { activeView, setActiveView } = useActiveView(views);
  const addView = (view: TrackerViewType) => {
    if (views.includes(view)) return;
    setViews([...views, view]);
    setActiveView(view);
    if (view !== 'table') {
      track('tracker_view_added', { tracker_id: trackerId, board_type: 'briefs', view_type: view });
    }
  };
  const removeView = (view: TrackerViewType) => {
    if (view === 'table') return;
    setViews(views.filter((v) => v !== view));
    track('tracker_view_removed', { tracker_id: trackerId, board_type: 'briefs', view_type: view });
  };
  return { activeView, setActiveView, addView, removeView };
}

/**
 * Detail page for a single tracker. The tracker row decides the board engine:
 * `briefs` renders the briefs board (rows are the workspace's briefs), a record
 * source renders the record board (rows come from the rows endpoint, a page at
 * a time), and anything else the free-form item board. The legacy
 * `projects-briefs` URL resolves to the seeded briefs tracker.
 */
export function TrackerBoard() {
  const { trackerId } = useParams<{ trackerId: string }>();

  if (!trackerId) return <Navigate to={ROUTES.TRACKERS} replace />;
  if (trackerId === 'projects-briefs') return <LegacyBriefsRedirect />;
  return <TrackerPage trackerId={trackerId} />;
}

/** Old bookmarks: /trackers/projects-briefs → the seeded briefs tracker. */
function LegacyBriefsRedirect() {
  const { t } = useTranslation('trackers');

  const listQuery = useQuery(organizationTrackersQueryOptions());
  if (listQuery.isPending) {
    return (
      <div className="flex h-40 items-center justify-center text-sm text-muted-foreground">
        {t('trackers.trackerBoard.loadingTrackers')}
      </div>
    );
  }
  const briefsTracker = listQuery.data?.find((t) => t.itemSource === 'briefs');
  return (
    <Navigate
      to={briefsTracker ? ROUTES.TRACKER_DETAIL(briefsTracker.id) : ROUTES.TRACKERS}
      replace
    />
  );
}

function TrackerPage({ trackerId }: { trackerId: string }) {
  const { t } = useTranslation('trackers');

  const navigate = useNavigate();
  // Trackers belong to the organization, so any of them opens under whichever
  // workspace is selected; opening one never switches workspace.
  const workspaceId = useCurrentWorkspaceId() ?? '';
  const trackerQuery = useQuery(trackerQueryOptions(workspaceId, trackerId));
  const failure = trackerQuery.isError ? trackerOpenFailure(trackerQuery.error) : null;
  const mergedInto = failure?.kind === 'merged' ? failure.targetTrackerId : null;

  useEffect(() => {
    if (mergedInto) void navigate(ROUTES.TRACKER_DETAIL(mergedInto), { replace: true });
  }, [mergedInto, navigate]);

  // Board-open engagement — the denominator for every other tracker event.
  // Fires once per tracker (keyed on id), whichever board engine renders.
  const openedItemSource = trackerQuery.data?.itemSource;
  useEffect(() => {
    if (!openedItemSource) return;
    track('tracker_opened', { tracker_id: trackerId, board_type: boardTypeOf(openedItemSource) });
  }, [trackerId, openedItemSource]);

  // A failed tracker fetch renders an inline not-found state rather than
  // auto-redirecting: on a hard page load the first fetches can fail
  // transiently while auth/workspace bootstrap completes, and a redirect
  // would wrongly bounce valid URLs back to the hub.
  if (failure?.kind === 'upgrading') {
    return (
      <ListPageLayout>
        <BreadcrumbPageHeader
          trail={[{ label: trackersPageTitle(), href: ROUTES.TRACKERS }]}
          page={t('trackers.trackerBoard.trackersUpgrading')}
        />
        <ListPageBody>
          <Empty role="status" className="flex-1" data-testid="trackers-upgrading-state">
            <EmptyHeader>
              <EmptyStateTitle>{t('trackers.trackerBoard.trackersUpgrading')}</EmptyStateTitle>
              <EmptyDescription>
                {t('trackers.trackerBoard.trackersUpgradingDescription')}
              </EmptyDescription>
            </EmptyHeader>
            <EmptyContent>
              <Button variant="outline" onClick={() => void trackerQuery.refetch()}>
                {t('trackers.tryAgain')}
              </Button>
            </EmptyContent>
          </Empty>
        </ListPageBody>
      </ListPageLayout>
    );
  }

  if (trackerQuery.isError && !mergedInto) {
    return (
      <ListPageLayout>
        <BreadcrumbPageHeader
          trail={[{ label: trackersPageTitle(), href: ROUTES.TRACKERS }]}
          page={t('trackers.trackerBoard.trackerNotFound')}
        />
        {/* The 56px header has no description row, and a dead link is the one
            case where the explanation cannot wait behind an info button. */}
        <ListPageBody>
          <Empty role="status" className="flex-1">
            <EmptyHeader>
              <EmptyStateTitle>{t('trackers.trackerBoard.trackerNotFound')}</EmptyStateTitle>
              <EmptyDescription>
                {t('trackers.trackerBoard.thisTrackerMayHaveBeen')}
              </EmptyDescription>
            </EmptyHeader>
          </Empty>
        </ListPageBody>
      </ListPageLayout>
    );
  }

  if (!trackerQuery.data) {
    return (
      <div className="flex h-40 items-center justify-center text-sm text-muted-foreground">
        {t('trackers.trackerBoard.loadingTracker')}
      </div>
    );
  }

  const tracker = trackerQuery.data;
  return (
    <TrackerAccessProvider canEdit={canEditTracker(tracker)} canManage={tracker.canManage}>
      {tracker.itemSource === 'briefs' ? (
        <BriefsTrackerBoard tracker={tracker} />
      ) : (
        <TrackerBoardPage tracker={tracker} />
      )}
    </TrackerAccessProvider>
  );
}

/**
 * Every tracker but the briefs board: the shared header over whichever engine
 * the item source calls for, free-form items or platform records. Both keep
 * their toolbar and views inside the board component, for parity with the
 * briefs board above.
 */
function TrackerBoardPage({ tracker }: { tracker: Tracker }) {
  const { t } = useTranslation('trackers');

  const recordBacked = isRecordSource(tracker.itemSource);
  // "Custom tracker" is the standing description of a free-form board and says
  // something false about a records one, which has no stand-in of its own: what
  // it holds is already on the board.
  const description =
    tracker.description ?? (recordBacked ? null : t('trackers.trackerBoard.customTracker'));

  return (
    <ListPageLayout>
      <TrackerBoardHeader tracker={tracker} description={description} />

      <StaggerItem index={2} className="flex min-h-0 flex-1 flex-col">
        <ListPageBody>
          {recordBacked ? (
            <RecordTrackerBoard tracker={tracker} />
          ) : (
            <CustomTrackerBoard tracker={tracker} />
          )}
        </ListPageBody>
      </StaggerItem>
    </ListPageLayout>
  );
}

/** The briefs board (rows are the workspace's briefs). */
function BriefsTrackerBoard({ tracker }: { tracker: Tracker }) {
  const { t } = useTranslation('trackers');

  const navigate = useNavigate();
  const board = useTrackersBoard(tracker);
  const workspaceId = useCurrentWorkspaceId() ?? '';
  const updateBriefValue = useUpdateAnyBriefCustomColumnValueMutation({ workspaceId });

  const { activeView, setActiveView, addView, removeView } = useBriefsBoardViews(
    tracker.id,
    board.views,
    board.setViews,
  );

  // `brief.projectId` is the workspace-api INTERNAL id, but project-scoped
  // routes are keyed by `platformProjectId`. The board owns the internal→platform
  // resolver; drafts (no platform id) resolve to undefined → their actions no-op.
  const { platformProjectIdByInternalId } = board;

  const handleBriefClick = useCallback(
    (brief: Brief) => {
      const platformProjectId = platformProjectIdByInternalId.get(brief.projectId);
      if (!platformProjectId) return;
      void navigate(ROUTES.PROJECT_BRIEF_DETAIL(platformProjectId, brief.id));
    },
    [navigate, platformProjectIdByInternalId],
  );

  // Lane assignment follows the ACTIVE grouping (the toolbar's group-by), taken
  // from the same `tableGroups` the table renders — so Kanban/Timeline swimlanes
  // match the table instead of always being per-project. First bucket wins for
  // multi-membership (a multi-owner brief lands in its first owner's lane).
  const laneByItemId = useMemo(() => {
    const map = new Map<
      string,
      { id: string; title: string | null; color?: string; index: number }
    >();
    board.tableGroups.forEach((g, index) => {
      for (const it of g.items) {
        if (!map.has(it.id)) map.set(it.id, { id: g.id, title: g.title, color: g.color, index });
      }
    });
    return map;
  }, [board.tableGroups]);

  // Kanban / Timeline adapters: flatten grouped briefs into the views'
  // source-agnostic item shape, color-coded per the active grouping.
  const boardItems = useMemo(
    () =>
      board.groups.flatMap((group) =>
        group.briefs.map((brief) => {
          const lane = laneByItemId.get(brief.id);
          const idx = lane?.index ?? 0;
          const color = lane?.color ?? GROUP_COLORS[idx % GROUP_COLORS.length];
          const title = lane?.title ?? t('trackers.trackerBoard.untitled');
          return {
            id: brief.id,
            title: brief.title,
            contextLabel: title,
            contextColor: color,
            chipLabel: brief.briefType ? BRIEF_TYPE_LABEL[brief.briefType] : undefined,
            // eslint-disable-next-line i18next/no-literal-string -- lane id fallback; laneTitle carries the copy
            laneId: lane?.id ?? 'ungrouped',
            laneTitle: title,
            laneColor: color,
            values: brief.customColumnValues as Record<string, unknown> | undefined,
            // Powers the Dashboard view's default "items over time" line.
            createdAt: brief.createdAt,
          };
        }),
      ),
    [board.groups, laneByItemId, t],
  );

  const briefById = useMemo(() => {
    const map = new Map<string, Brief>();
    for (const group of board.groups) for (const brief of group.briefs) map.set(brief.id, brief);
    return map;
  }, [board.groups]);

  const statusColumn = useMemo(
    () => board.customColumns.find((c) => c.type === 'status'),
    [board.customColumns],
  );

  const handleSetStatus = useCallback(
    (itemId: string, statusId: string | null) => {
      const brief = briefById.get(itemId);
      const platformProjectId = brief && platformProjectIdByInternalId.get(brief.projectId);
      if (!brief || !statusColumn || !platformProjectId) return;
      updateBriefValue.mutate({
        projectId: platformProjectId,
        briefId: brief.id,
        columnId: statusColumn.id,
        value: statusId,
      });
    },
    [briefById, statusColumn, updateBriefValue, platformProjectIdByInternalId],
  );

  const handleItemClick = useCallback(
    (itemId: string) => {
      const brief = briefById.get(itemId);
      if (brief) handleBriefClick(brief);
    },
    [briefById, handleBriefClick],
  );

  // Single StatusConfigModal at the page level. The Configure menu item in
  // any group's column-header kebab opens it for that column id.
  const [configuringColumnId, setConfiguringColumnId] = useState<string | null>(null);
  const configuringColumn = configuringColumnId
    ? board.customColumns.find((c) => c.id === configuringColumnId)
    : undefined;

  // The vertical scroll container the board rows virtualize against.
  const boardScrollRef = useRef<HTMLDivElement>(null);
  // Custom-groups editor (Group by: Custom).
  const [groupsEditorOpen, setGroupsEditorOpen] = useState(false);

  // Single comments side panel, URL-deeplinked per row. The Comments column
  // cell on any brief row calls onOpenComments(briefId) → sets `?item=`.
  const { openRowKey, focusCommentId, openComments, closeComments } = useCommentDeeplink();

  // Full-screen empty state ONLY when the workspace has zero briefs and the
  // user has nothing applied that could narrow the view.
  const showEmptyState =
    !board.isLoading && board.unfilteredTotalBriefs === 0 && !board.hasActiveFilters;

  return (
    <>
      <ListPageLayout>
        <TrackerBoardHeader
          tracker={tracker}
          description={
            tracker.description ?? t('trackers.trackerBoard.allBriefsAcrossYourProjects')
          }
        />

        <StaggerItem index={2} className="flex min-h-0 flex-1 flex-col">
          <ListPageBody>
            {showEmptyState ? (
              <TrackersEmptyState />
            ) : (
              <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-2xl bg-card">
                <TrackersToolbar
                  searchValue={board.searchInput}
                  onSearchChange={board.setSearchInput}
                  sortId={board.sortId}
                  onSortChange={(next) => {
                    board.setSortId(next);
                    if (next) {
                      track('tracker_sorted', {
                        tracker_id: tracker.id,
                        board_type: 'briefs',
                        source: 'toolbar',
                        direction: next.endsWith('-desc') ? 'desc' : 'asc',
                      });
                    }
                  }}
                  groupByOptions={board.groupByOptions}
                  groupByValue={board.groupById}
                  onGroupByChange={(next) => {
                    board.setGroupBy(next);
                    track('tracker_grouped', {
                      tracker_id: tracker.id,
                      board_type: 'briefs',
                      group_by: !next
                        ? 'none'
                        : next === 'project'
                          ? 'project'
                          : next === board.customGroupByValue
                            ? 'custom'
                            : (board.customColumns.find((c) => c.id === next)?.type ?? 'none'),
                    });
                    // Selecting Custom with no groups yet → open the editor.
                    if (
                      canEditTracker(tracker) &&
                      next === board.customGroupByValue &&
                      board.customGroups.length === 0
                    ) {
                      setGroupsEditorOpen(true);
                    }
                  }}
                  filterColumns={board.filterColumns}
                  filters={board.filters}
                  onFiltersChange={(next) => {
                    // Emit only for newly-enabled columns (resolvable type).
                    for (const key of Object.keys(next)) {
                      if (key in board.filters) continue;
                      const columnType = board.filterColumnType(key);
                      if (columnType) {
                        track('tracker_filtered', {
                          tracker_id: tracker.id,
                          board_type: 'briefs',
                          column_type: columnType,
                        });
                      }
                    }
                    board.setFilters(next);
                  }}
                  filterPanel={{
                    trackerId: tracker.id,
                    rules: board.filterRules,
                    activeFilterCount: board.activeFilterCount,
                    onRulesChange: (next) => {
                      for (const columnId of newlyNarrowingColumnIds(board.filterRules, next)) {
                        const columnType = board.filterColumnType(columnId);
                        if (columnType) {
                          track('tracker_filtered', {
                            tracker_id: tracker.id,
                            board_type: 'briefs',
                            column_type: columnType,
                          });
                        }
                      }
                      board.setFilterRules(next);
                    },
                    counts: board.filterValueCounts,
                    visibleItems: board.totalBriefs,
                    totalItems: board.unfilteredTotalBriefs,
                    recentColumnIds: board.recentFilterColumns,
                    onColumnUsed: board.noteFilterColumnUsed,
                  }}
                  trailing={
                    <BoardViewTabs
                      views={board.views}
                      activeView={activeView}
                      onSwitch={(view) => {
                        setActiveView(view);
                        track('tracker_view_switched', {
                          tracker_id: tracker.id,
                          board_type: 'briefs',
                          view_type: view,
                        });
                      }}
                      onAddView={addView}
                      onRemoveView={removeView}
                      onReorder={board.setViews}
                    />
                  }
                />
                {/* No bottom padding: the board's sticky horizontal scrollbar
                  pins to bottom-0 of this scroll area, so any padding-bottom
                  here would leave a gap where rows peek out below the bar. */}
                <div
                  ref={boardScrollRef}
                  className={`flex-1 overflow-y-auto px-4 ${activeView === 'table' ? '' : 'py-4'}`}
                >
                  {activeView === 'kanban' ? (
                    <KanbanView
                      items={boardItems}
                      customColumns={board.customColumns}
                      onItemClick={handleItemClick}
                      onSetStatus={handleSetStatus}
                      filtered={board.hasActiveFilters}
                    />
                  ) : null}
                  {activeView === 'timeline' ? (
                    <TimelineView
                      items={boardItems}
                      customColumns={board.customColumns}
                      onItemClick={handleItemClick}
                    />
                  ) : null}
                  {activeView === 'dashboard' ? (
                    <DashboardView
                      trackerId={tracker.id}
                      boardType="briefs"
                      items={boardItems}
                      customColumns={board.customColumns}
                      dashboard={board.dashboard}
                      onDashboardChange={board.setDashboard}
                    />
                  ) : null}
                  {activeView === 'table' ? (
                    <TrackersBoardTable
                      groups={board.tableGroups}
                      groupIds={board.tableGroupIds}
                      isLoading={board.isLoading}
                      expandedGroupIds={board.expandedProjectIds}
                      onToggleGroup={board.toggleProjectExpanded}
                      onGroupDragStart={board.handleGroupDragStart}
                      onGroupDragEnd={board.handleGroupDragEnd}
                      onReorderItems={board.reorderBriefs}
                      {...(board.isCustomGrouping
                        ? {
                            // Drag a brief into a group → set its `__group__`.
                            onMoveItem: (id: string, toGroupId: string) =>
                              board.moveBriefToGroup(id, toGroupId),
                            onAddGroup: () => {
                              board.addGroup();
                              track('tracker_custom_group_created', {
                                tracker_id: tracker.id,
                                board_type: 'briefs',
                              });
                            },
                            onRenameGroup: board.renameGroup,
                            onDeleteGroup: board.deleteGroup,
                          }
                        : {})}
                      columnOrder={board.columnOrder}
                      columnWidths={board.columnWidths}
                      onReorderColumns={board.reorderColumns}
                      onSetColumnWidth={board.setColumnWidth}
                      itemColumnWidth={board.itemColumnWidth}
                      onSetItemColumnWidth={board.setItemColumnWidth}
                      scrollContainersRef={board.scrollContainersRef}
                      boardScrollRef={boardScrollRef}
                      syncedScrollLeftRef={board.syncedScrollLeftRef}
                      tableMinContentWidth={board.tableMinContentWidth}
                      onAddCustomColumn={(arg) => {
                        board.addCustomColumn(arg);
                        track('tracker_column_added', {
                          tracker_id: tracker.id,
                          board_type: 'briefs',
                          column_type: arg.type,
                        });
                      }}
                      onAddCustomColumnAfter={(afterId, arg) => {
                        board.addCustomColumnAfter(afterId, arg);
                        track('tracker_column_added', {
                          tracker_id: tracker.id,
                          board_type: 'briefs',
                          column_type: arg.type,
                        });
                      }}
                      onDuplicateColumn={board.duplicateColumn}
                      onDeleteColumn={(id) => {
                        const columnType = board.customColumns.find((c) => c.id === id)?.type;
                        board.deleteColumn(id);
                        if (columnType) {
                          track('tracker_column_deleted', {
                            tracker_id: tracker.id,
                            board_type: 'briefs',
                            column_type: columnType,
                          });
                        }
                      }}
                      onConfigureColumn={setConfiguringColumnId}
                      columnSort={board.columnSort}
                      onToggleColumnSort={(id) => {
                        const cur = board.columnSort;
                        // Header sort cycles asc → desc → off.
                        /* eslint-disable i18next/no-literal-string -- sort direction values sent as an analytics property */
                        const direction =
                          cur && cur.columnId === id
                            ? cur.dir === 'asc'
                              ? 'desc'
                              : 'none'
                            : 'asc';
                        /* eslint-enable i18next/no-literal-string */
                        board.toggleColumnSort(id);
                        track('tracker_sorted', {
                          tracker_id: tracker.id,
                          board_type: 'briefs',
                          source: 'header',
                          direction,
                        });
                      }}
                      columnDefs={board.columnDefs}
                      customColumns={board.customColumns}
                      onRenameColumn={board.renameColumn}
                      onItemClick={(id) => {
                        const brief = briefById.get(id);
                        if (brief) handleBriefClick(brief);
                      }}
                      trackerId={tracker.id}
                      onOpenComments={openComments}
                      openCommentsRowKey={openRowKey}
                    />
                  ) : null}
                </div>
              </div>
            )}
          </ListPageBody>
        </StaggerItem>
      </ListPageLayout>

      <StatusConfigModal
        open={configuringColumn?.type === 'status'}
        onOpenChange={(next) => {
          if (!next) setConfiguringColumnId(null);
        }}
        options={configuringColumn?.statusOptions ?? []}
        onSave={(next) => {
          if (configuringColumnId) {
            board.updateStatusOptions(configuringColumnId, next);
            track('tracker_column_status_configured', {
              tracker_id: tracker.id,
              board_type: 'briefs',
              option_count: next.length,
            });
          }
        }}
      />
      <ButtonConfigModal
        open={configuringColumn?.type === 'button'}
        onOpenChange={(next) => {
          if (!next) setConfiguringColumnId(null);
        }}
        caption={configuringColumn?.buttonCaption ?? configuringColumn?.label ?? ''}
        onSave={(next) => {
          if (configuringColumnId) board.updateButtonCaption(configuringColumnId, next);
        }}
      />
      <FormulaConfigModal
        open={configuringColumn?.type === 'formula'}
        onOpenChange={(next) => {
          if (!next) setConfiguringColumnId(null);
        }}
        expression={configuringColumn?.formulaExpression ?? ''}
        columns={board.customColumns.filter((c) => c.id !== configuringColumnId)}
        onSave={(next) => {
          if (configuringColumnId) board.updateFormulaExpression(configuringColumnId, next);
        }}
      />
      <StatusConfigModal
        open={groupsEditorOpen}
        onOpenChange={setGroupsEditorOpen}
        title={t('trackers.trackerBoard.configureGroups')}
        options={
          board.customGroups.length > 0
            ? board.customGroups
            : [
                {
                  id: 'group_untitled',
                  label: t('trackers.trackerBoard.untitledGroup'),

                  color: '#3b82f6',
                },
              ]
        }
        onSave={board.setCustomGroups}
      />
      <TrackerCommentsPanel
        open={openRowKey !== null}
        trackerId={tracker.id}
        boardType="briefs"
        rowKey={openRowKey}
        focusCommentId={focusCommentId}
        onClose={closeComments}
      />
    </>
  );
}
